import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Upload, Loader2, CheckCircle2, ShieldCheck, FileText, ArrowRight, ExternalLink, Plus, Link2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TIERS = {
  starter: { name: "Single Reel", price: 20 },
  growth: { name: "Growth Bundle", price: 55 },
  domination: { name: "Domination Pack", price: 100 },
};

const CUR = { USD: "$", INR: "₹", EUR: "€", GBP: "£" };

export const OrderModal = ({ open, onClose, initialTier = "growth", initialUrl = "" }) => {
  const [tier, setTier] = useState(initialTier);
  const [form, setForm] = useState({ product_url: "", brand_guidelines: "", name: "", email: "", phone: "", extra_format: false, extra_format_note: "", comments: "" });
  const [refLinks, setRefLinks] = useState([""]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("form"); // form | pay | success
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTier(initialTier || "growth");
      setForm((f) => ({ ...f, product_url: initialUrl || "" }));
      setStage("form");
      setOrder(null);
    }
  }, [open, initialTier, initialUrl]);

  useEffect(() => {
    if (document.getElementById("razorpay-checkout-js")) return;
    const s = document.createElement("script");
    s.id = "razorpay-checkout-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  if (!open) return null;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of picked) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, name: data.filename });
      }
      setFiles((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submitOrder = async () => {
    if (!form.product_url || !form.name || !form.email) {
      toast.error("Please fill product URL, name and email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        tier, product_url: form.product_url, brand_guidelines: form.brand_guidelines,
        name: form.name, email: form.email, phone: form.phone, file_ids: files.map((f) => f.id),
        extra_format: form.extra_format, extra_format_note: form.extra_format_note, comments: form.comments,
        reference_links: refLinks.map((l) => l.trim()).filter(Boolean),
      });
      const { data: pay } = await api.post("/payments/create", { order_id: data.order_id });
      setOrder({ ...data, pay });
      setStage("pay");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    const pay = order.pay;
    if (!window.Razorpay) {
      toast.error("Payment library still loading, please try again");
      return;
    }
    const rzp = new window.Razorpay({
      key: pay.key_id,
      amount: pay.amount,
      currency: pay.currency,
      name: "iTMunch Viral Videos",
      description: `${pay.tier_name} — viral video order`,
      order_id: pay.payment_order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#FF2E63" },
      handler: async (resp) => {
        setSubmitting(true);
        try {
          await api.post("/payments/verify", {
            order_id: order.order_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setStage("success");
          toast.success("Payment successful — order confirmed!");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
        } finally {
          setSubmitting(false);
        }
      },
      modal: { ondismiss: () => toast.message("Checkout closed — your order is saved, you can pay anytime.") },
    });
    rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
    rzp.open();
  };

  const price = TIERS[tier].price + (form.extra_format ? 10 : 0);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="order-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-[#12141C] border border-white/10 shadow-2xl">
        <div className="sticky top-0 itm-glass border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">
            {stage === "form" && "Create your viral video"}
            {stage === "pay" && "Secure checkout"}
            {stage === "success" && "You're all set! 🎉"}
          </h3>
          <button onClick={onClose} data-testid="order-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {stage === "form" && (
            <div className="space-y-5">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Package</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {Object.entries(TIERS).map(([id, t]) => (
                    <button key={id} data-testid={`order-tier-${id}`} onClick={() => setTier(id)}
                      className={`rounded-lg border px-2 py-3 text-center transition-colors ${
                        tier === id ? "border-[#FF2E63] bg-[#FF2E63]/10" : "border-white/10 hover:border-white/30"
                      }`}>
                      <p className="text-sm font-semibold">{t.name}</p>
                      <p className="font-mono text-[#00F5D4] text-sm">${t.price}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Product / service page URL *" testid="order-product-url-input"
                value={form.product_url} onChange={upd("product_url")} placeholder="https://yourbrand.com/product" />

              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Brand guidelines (or add link to access brand guidelines)</label>
                <textarea data-testid="order-brand-guidelines-input" value={form.brand_guidelines} onChange={upd("brand_guidelines")}
                  rows={3} placeholder="Colors, tone, must-have messaging, do's & don'ts… or paste a link (Google Drive, Canva, Notion)"
                  className="mt-2 w-full rounded-lg bg-[#0A0B10] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FF2E63]/60" />
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Logo & brand assets</label>
                <div onClick={() => fileRef.current?.click()}
                  className="mt-2 cursor-pointer rounded-lg border border-dashed border-white/15 hover:border-[#FF2E63]/50 px-4 py-6 text-center transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FF2E63]" />
                    : <Upload className="w-5 h-5 mx-auto text-slate-500" />}
                  <p className="mt-2 text-sm text-slate-400">Click to upload (logos, images, PDFs — max 25MB)</p>
                  <input ref={fileRef} data-testid="order-file-upload-input" type="file" multiple hidden onChange={handleFiles} />
                </div>
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((f) => (
                      <div key={f.id} className="flex items-center gap-2 text-xs text-slate-300">
                        <FileText className="w-3.5 h-3.5 text-[#00F5D4]" /> {f.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Comments & expectations</label>
                <textarea data-testid="order-comments-input" value={form.comments} onChange={upd("comments")}
                  rows={3} maxLength={3000} placeholder="What should the video achieve? Target audience, key message, style or reference reels you like…"
                  className="mt-2 w-full rounded-lg bg-[#0A0B10] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FF2E63]/60" />
              </div>

              <ReferenceLinks links={refLinks} setLinks={setRefLinks} />

              <div className="grid grid-cols-2 gap-3">
                <Field label="Your name *" testid="order-name-input" value={form.name} onChange={upd("name")} placeholder="Jane Doe" />
                <Field label="Phone" testid="order-phone-input" value={form.phone} onChange={upd("phone")} placeholder="+1 555 000 0000" />
              </div>
              <Field label="Email *" testid="order-email-input" value={form.email} onChange={upd("email")} placeholder="you@brand.com" type="email" />

              <div className="rounded-lg border border-white/10 bg-[#0A0B10] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="order-extra-format-checkbox" checked={form.extra_format}
                    onChange={(e) => setForm({ ...form, extra_format: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-[#FF2E63]" />
                  <span>
                    <span className="text-sm font-semibold text-white">Add a different size <span className="text-[#00F5D4]">+$10</span></span>
                    <span className="block text-xs text-slate-400 mt-0.5">Get the same video resized for another platform (YouTube 16:9, Shorts, etc.)</span>
                  </span>
                </label>
                {form.extra_format && (
                  <input data-testid="order-extra-format-note-input" value={form.extra_format_note} onChange={upd("extra_format_note")}
                    placeholder="Which format(s)? e.g. YouTube 16:9, 1:1 square"
                    className="mt-3 w-full rounded-lg bg-[#12141C] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FF2E63]/60" />
                )}
              </div>

              <button data-testid="order-continue-button" onClick={submitOrder} disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF2E63] hover:bg-[#E02554] disabled:opacity-60 text-white font-semibold py-3 transition-colors">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to payment — ${price} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {stage === "pay" && order && (
            <div className="space-y-5">
              <div className="rounded-xl bg-[#0A0B10] border border-white/10 p-5">
                <div className="flex justify-between text-sm"><span className="text-slate-400">Package</span><span className="font-semibold">{order.tier_name}</span></div>
                <div className="flex justify-between text-sm mt-2"><span className="text-slate-400">Order ID</span><span className="font-mono text-xs">{order.order_id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between items-end mt-4 pt-4 border-t border-white/10">
                  <span className="text-slate-400">Total due</span>
                  <span className="font-display text-3xl font-extrabold text-[#00F5D4]">
                    {CUR[order.pay.currency] || "$"}{order.pay.amount / 100}
                  </span>
                </div>
              </div>

              <button data-testid="order-submit-razorpay-button" onClick={payNow} disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00F5D4] hover:bg-[#00d9bd] disabled:opacity-60 text-[#0A0B10] font-bold py-3 transition-colors">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay {CUR[order.pay.currency] || "$"}{order.pay.amount / 100} via Razorpay</>}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5" /> Encrypted & secure checkout
              </p>
              <p data-testid="order-refund-notice" className="text-center text-xs text-slate-500">
                By paying you agree to our <a href="/refund-policy" target="_blank" rel="noreferrer" className="text-[#00F5D4] hover:underline">Refund Policy</a>: no refunds, replacements or revisions.
              </p>
            </div>
          )}

          {stage === "success" && order && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 mx-auto text-[#00F5D4]" />
              <h4 className="mt-4 font-display text-xl font-bold">Order confirmed!</h4>
              <p className="mt-2 text-sm text-slate-400">We've emailed your confirmation. Your viral video is now in production and will land in your inbox soon.</p>
              <div className="mt-4 rounded-lg bg-[#0A0B10] border border-white/10 px-4 py-3 font-mono text-sm">
                Order #{order.order_id.slice(0, 8).toUpperCase()}
              </div>
              <a href={`/order/${order.order_id}`} data-testid="order-track-link"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FF2E63] hover:bg-[#E02554] text-white font-semibold px-6 py-2.5 transition-colors">
                Track my order <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Field = ({ label, testid, type = "text", ...props }) => (
  <div>
    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</label>
    <input type={type} data-testid={testid} {...props}
      className="mt-2 w-full rounded-lg bg-[#0A0B10] border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FF2E63]/60 transition-colors" />
  </div>
);

const ReferenceLinks = ({ links, setLinks }) => {
  const update = (i, v) => setLinks(links.map((l, j) => (j === i ? v : l)));
  const remove = (i) => setLinks(links.length > 1 ? links.filter((_, j) => j !== i) : [""]);
  return (
    <div data-testid="order-reference-links">
      <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Videos you like (reference reels)</label>
      <p className="mt-1 text-xs text-slate-500">Paste links to Instagram reels, YouTube Shorts or TikToks you'd like your video to look similar to.</p>
      <div className="mt-2 space-y-2">
        {links.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 text-slate-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="url" value={l} onChange={(e) => update(i, e.target.value)} data-testid={`order-reference-link-input-${i}`}
                placeholder="https://www.instagram.com/reel/..."
                className="w-full rounded-lg bg-[#0A0B10] border border-white/10 pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FF2E63]/60 transition-colors" />
            </div>
            {(links.length > 1 || l) && (
              <button type="button" onClick={() => remove(i)} data-testid={`order-reference-link-remove-${i}`}
                className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
      {links.length < 5 && (
        <button type="button" onClick={() => setLinks([...links, ""])} data-testid="order-reference-link-add-button"
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#00F5D4] hover:text-white transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add another link
        </button>
      )}
    </div>
  );
};
